/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useImperativeHandle, forwardRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import styled from 'styled-components';
import { JobContext } from 'context/jobContextContainer';
import { CallContext } from 'context/callContextContainer';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { jobsService } from 'services/jobsService';

const moment = require('moment-timezone');

const TableInfoContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #666;
`;

const tableCellPadding = '4px 6px';

const StyledTableContainer = styled(TableContainer)`
  max-height: 500px;
  overflow: auto;
  & .MuiTableCell-root {
    padding: ${tableCellPadding};
  }
`;

const StyledTableHeadCell = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCell = styled(TableCell)`
  font-size: 12px;
  white-space: nowrap;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellWrap = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  padding: ${tableCellPadding} !important;
`;

const StyledTableHeadCellWrap = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: normal;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellTime = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  max-width: 80px;
  padding: ${tableCellPadding} !important;
`;

const StyledTableHeadCellTime = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: normal;
  max-width: 80px;
  padding: ${tableCellPadding} !important;
`;

const JobStep = forwardRef((props, ref) => {
  const { routePlanTempJobIds, routePlanData, setRoutePlanData } = useContext(JobContext);
  const { siteSetting } = useContext(CallContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [showWillCallDialog, setShowWillCallDialog] = useState(false);
  const [willCallJobIds, setWillCallJobIds] = useState([]);

  const validate = () => {
    if (!selected || selected.length === 0) {
      setAlertMessage('Please select at least one job to continue');
      setShowAlert(true);
      return false;
    }

    // Check for Will Call jobs (willcall_status = '1')
    const selectedWillCallJobs = jobs.filter(
      job => selected.includes(job.id) && job.willcall_status === '1'
    );

    if (selectedWillCallJobs.length > 0) {
      const willCallIds = selectedWillCallJobs.map(job => job.id);
      setWillCallJobIds(willCallIds);
      setShowWillCallDialog(true);
      return false;
    }

    // Store selected jobs in routePlanData when validation passes
    const selectedJobs = jobs.filter(job => selected.includes(job.id));
    setRoutePlanData({
      ...routePlanData,
      jobs: selectedJobs,
    });

    return true;
  };

  const handleDeselectWillCallJobs = () => {
    const newSelected = selected.filter(id => !willCallJobIds.includes(id));
    setSelected(newSelected);
    setShowWillCallDialog(false);
    setWillCallJobIds([]);
  };

  const handleCloseWillCallDialog = () => {
    setShowWillCallDialog(false);
    setWillCallJobIds([]);
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  useEffect(() => {
    const fetchJobs = async () => {
      if (routePlanTempJobIds.length === 0) {
        setJobs([]);
        setSelected([]);
        return;
      }

      setLoading(true);
      showCircularProgress('Loading jobs...');

      try {
        // Convert array to comma-separated string if needed, or pass as array
        const ids = Array.isArray(routePlanTempJobIds)
          ? routePlanTempJobIds.join(',')
          : routePlanTempJobIds;

        const response = await jobsService.get_jobs_by_ids({ ids });
        if (response?.data?.status === 'SUCCESS' && response?.data?.data) {
          const jobsData = Array.isArray(response.data.data) ? response.data.data : [];
          setJobs(jobsData);
          // Select all jobs by default
          const allJobIds = jobsData.map(job => job.id);
          setSelected(allJobIds);
        } else {
          setAlertMessage('Failed to fetch jobs');
          setShowAlert(true);
          setJobs([]);
          setSelected([]);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setAlertMessage('Error fetching jobs');
        setShowAlert(true);
        setJobs([]);
        setSelected([]);
      } finally {
        setLoading(false);
        hideCircularProgress();
      }
    };

    fetchJobs();
  }, [routePlanTempJobIds]);

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString || !moment(dateString).isValid()) {
      return '-';
    }
    if (!siteSetting()) {
      return '-';
    }
    const format = includeTime ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY';
    return moment.utc(dateString).tz(siteSetting().timezone).format(format);
  };

  const formatDateTwoLines = (dateString) => {
    if (!dateString || !moment(dateString).isValid()) {
      return { date: '-', time: '' };
    }
    if (!siteSetting()) {
      return { date: '-', time: '' };
    }
    const m = moment.utc(dateString).tz(siteSetting().timezone);
    return {
      date: m.format('MM/DD/YYYY'),
      time: m.format('hh:mm A'),
    };
  };

  const getStatusText = (status) => {
    const statusMap = {
      0: 'CFS',
      1: 'Dispatched',
      2: 'Accepted',
      3: 'Declined',
      4: '@Pickup',
      5: 'Picked Up',
      6: 'Drop-Off',
      7: 'Pay',
      8: 'Complete',
      9: 'No Show',
      10: 'Cancelled',
      11: 'Backlog',
    };
    return statusMap[status] || 'Unknown';
  };

  const formatAddress = (location) => {
    if (!location) return '-';
    
    return [location.location_name, location.addr1, location.city, location.state]
      .filter(str => str?.length > 0)
      .join(', ') || '-';
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allJobIds = jobs.map(job => job.id);
      setSelected(allJobIds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, jobId) => {
    const selectedIndex = selected.indexOf(jobId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, jobId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (jobId) => selected.indexOf(jobId) !== -1;
  const numSelected = selected.length;
  const rowCount = jobs.length;

  if (loading) {
    return <div>Loading jobs...</div>;
  }

  if (routePlanTempJobIds.length === 0) {
    return <div>No jobs selected</div>;
  }

  return (
    <>
      <Dialog
        open={showAlert}
        onClose={() => setShowAlert(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Error</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAlert(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showWillCallDialog}
        onClose={handleCloseWillCallDialog}
        aria-labelledby="willcall-dialog-title"
        aria-describedby="willcall-dialog-description"
      >
        <DialogTitle id="willcall-dialog-title">Cannot Proceed with Will Call Jobs</DialogTitle>
        <DialogContent>
          <DialogContentText id="willcall-dialog-description">
            You can't proceed with Will Call jobs. Do you want to deselect all the Will Call jobs?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWillCallDialog} color="primary">
            No
          </Button>
          <Button onClick={handleDeselectWillCallJobs} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <TableInfoContainer>
        <span>
          {numSelected} / {rowCount} jobs selected
        </span>
      </TableInfoContainer>
      <StyledTableContainer>
        <Table aria-label="job selection table" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell padding="checkbox" align="center">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'select all jobs' }}
                />
              </StyledTableHeadCell>
              <StyledTableHeadCell align="center">Job #</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Status</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Passenger</StyledTableHeadCell>
              <StyledTableHeadCellTime align="center">Pickup Time</StyledTableHeadCellTime>
              <StyledTableHeadCellTime align="center">Drop Off Time</StyledTableHeadCellTime>
              <StyledTableHeadCellWrap align="center">Pickup Address</StyledTableHeadCellWrap>
              <StyledTableHeadCellWrap align="center">Drop Off Address</StyledTableHeadCellWrap>
              <StyledTableHeadCell align="center">Fare</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Distance</StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={9} align="center">
                  No jobs found
                </StyledTableCell>
              </TableRow>
            ) : (
              jobs.map((job, index) => {
                const isItemSelected = isSelected(job.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    key={`${job.id}_${index}`}
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    onClick={(event) => handleClick(event, job.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, job.id)}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center" id={labelId}>
                      {job.id}
                    </StyledTableCell>
                    <StyledTableCell align="center">{getStatusText(job.status)}</StyledTableCell>
                    <StyledTableCell align="center">{job.passenger_name}</StyledTableCell>
                    <StyledTableCellTime align="center">
                      {job.willcall_status == '1' ? (
                        ' (Will Call)'
                      ) : (
                        (() => {
                          const { date, time } = formatDateTwoLines(job.reservation_time);
                          return (
                            <>
                              <div>{date}</div>
                              {time && <div>{time}</div>}
                            </>
                          );
                        })()
                      )}
                    </StyledTableCellTime>
                    <StyledTableCellTime align="center">
                      {job.willcall_status == '1' ? (
                        ' (Will Call)'
                      ) : (
                        (() => {
                          const { date, time } = formatDateTwoLines(job.reservation_end_time);
                          return (
                            <>
                              <div>{date}</div>
                              {time && <div>{time}</div>}
                            </>
                          );
                        })()
                      )}
                    </StyledTableCellTime>
                    <StyledTableCellWrap align="center">{formatAddress(job.pickup)}</StyledTableCellWrap>
                    <StyledTableCellWrap align="center">{formatAddress(job.dropoff)}</StyledTableCellWrap>
                    <StyledTableCell align="center">{job.fare ? `$${parseFloat(job.fare).toFixed(2)}` : '-'}</StyledTableCell>
                    <StyledTableCell align="center">
                      {job.distance ? `${parseFloat(job.distance).toFixed(2)} mi` : '-'}
                    </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
});

JobStep.displayName = 'JobStep';

export default JobStep;
